import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Link2, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";

function inviteUrl(token) {
  return `${window.location.origin}/invite/${token}`;
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // fallback: select-and-copy via temporary input
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <Button
      type="button" size="sm" variant="outline"
      className="border-[#E2E8F0] text-[#2E3A46]"
      onClick={onCopy}
      data-testid="invite-copy-btn"
    >
      {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
      {copied ? "Copied" : "Copy link"}
    </Button>
  );
}

export default function InvitationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", organization: "", expires_in_days: 7, note: "" });
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invitations");
      setItems(data);
    } catch {
      toast.error("Could not load invitations");
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      const payload = {
        email: form.email || null,
        organization: form.organization || null,
        note: form.note || null,
        expires_in_days: Number(form.expires_in_days) || 7,
      };
      const { data } = await api.post("/admin/invitations", payload);
      setLastCreated(data);
      setForm({ email: "", organization: "", expires_in_days: 7, note: "" });
      refresh();
      toast.success("Invitation link created");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create");
    }
    setCreating(false);
  };

  const onRevoke = async (id) => {
    try {
      await api.delete(`/admin/invitations/${id}`);
      toast.success("Invitation revoked");
      refresh();
    } catch {
      toast.error("Revoke failed");
    }
  };

  const statusBadge = (s) => {
    const map = {
      active: "border-[#4A7C59]/30 bg-[#4A7C59]/10 text-[#4A7C59]",
      used: "border-[#E2E8F0] bg-[#F8F9FA] text-[#5C6873]",
      revoked: "border-[#9E4747]/30 bg-[#9E4747]/10 text-[#9E4747]",
      expired: "border-[#E2E8F0] bg-[#F8F9FA] text-[#5C6873]",
    };
    return (
      <Badge variant="outline" className={`rounded-full font-body text-[11px] capitalize ${map[s] || ""}`}>
        {s}
      </Badge>
    );
  };

  return (
    <div data-testid="invitations-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-semibold text-[#1A2024]">Invitation links</h2>
          <p className="font-body text-sm text-[#5C6873]">
            Generate one-time pre-approved links and send them to colleagues you trust. Anyone who registers via the
            link becomes an active authority immediately — no further admin review needed.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setLastCreated(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E3A46] text-white hover:bg-[#1A2024]" data-testid="invite-new-btn">
              <Plus size={16} className="mr-1" /> New invitation link
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="invite-dialog">
            {!lastCreated ? (
              <form onSubmit={onCreate} className="space-y-4">
                <DialogHeader>
                  <DialogTitle className="font-heading text-[#1A2024]">Create invitation link</DialogTitle>
                </DialogHeader>
                <div>
                  <Label className="font-body text-sm text-[#1A2024]">Restrict to email (optional)</Label>
                  <Input type="email" placeholder="colleague@org.eu" value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="mt-1.5 h-11" data-testid="invite-form-email" />
                  <p className="mt-1 font-body text-xs text-[#5C6873]">If set, only this address can use the link.</p>
                </div>
                <div>
                  <Label className="font-body text-sm text-[#1A2024]">Organisation (optional)</Label>
                  <Input value={form.organization}
                    onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}
                    className="mt-1.5 h-11" data-testid="invite-form-organization" />
                </div>
                <div>
                  <Label className="font-body text-sm text-[#1A2024]">Expires in (days)</Label>
                  <Input type="number" min={1} max={30} value={form.expires_in_days}
                    onChange={(e) => setForm((f) => ({ ...f, expires_in_days: e.target.value }))}
                    className="mt-1.5 h-11" data-testid="invite-form-expires" />
                </div>
                <div>
                  <Label className="font-body text-sm text-[#1A2024]">Internal note (optional)</Label>
                  <Textarea value={form.note}
                    onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    className="min-h-[70px]" data-testid="invite-form-note" />
                </div>
                {error && <div className="rounded-md border border-[#9E4747]/30 bg-[#9E4747]/5 px-3 py-2 font-body text-sm text-[#9E4747]" data-testid="invite-form-error">{error}</div>}
                <DialogFooter>
                  <Button type="submit" disabled={creating} className="bg-[#2E3A46] text-white hover:bg-[#1A2024]" data-testid="invite-create-btn">
                    {creating ? "Creating…" : "Create link"}
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              <div className="space-y-4" data-testid="invite-created-view">
                <DialogHeader>
                  <DialogTitle className="font-heading text-[#1A2024]">Invitation link created</DialogTitle>
                </DialogHeader>
                <p className="font-body text-sm text-[#5C6873]">
                  Share this link with your colleague. It is single-use and expires on{" "}
                  <strong>{new Date(lastCreated.expires_at).toLocaleString()}</strong>.
                </p>
                <div className="flex items-center gap-2 rounded-md border border-[#E2E8F0] bg-[#F8F9FA] p-3 font-body text-xs text-[#1A2024] break-all" data-testid="invite-created-url">
                  <Link2 size={14} className="flex-shrink-0 text-[#6B7F82]" />
                  {inviteUrl(lastCreated.token)}
                </div>
                <DialogFooter>
                  <CopyButton value={inviteUrl(lastCreated.token)} />
                  <Button variant="outline" className="border-[#E2E8F0]" onClick={() => setOpen(false)}>Done</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 space-y-3">
        {loading && <p className="font-body text-sm text-[#5C6873]">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-8 text-center font-body text-sm text-[#5C6873]" data-testid="invitations-empty">
            No invitations yet.
          </div>
        )}
        {items.map((inv) => (
          <div key={inv.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`invite-row-${inv.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {statusBadge(inv.status)}
                <span className="font-body text-sm text-[#1A2024]">
                  {inv.email || <span className="text-[#5C6873]">any email</span>}
                </span>
              </div>
              <div className="font-body text-xs text-[#5C6873]">
                {inv.organization || "no org"} · expires {new Date(inv.expires_at).toLocaleDateString()}
                {inv.note ? ` · ${inv.note}` : ""}
              </div>
            </div>
            <div className="flex gap-2">
              {inv.status === "active" && (
                <>
                  <CopyButton value={inviteUrl(inv.token)} />
                  <Button size="sm" variant="outline" className="border-[#9E4747]/40 text-[#9E4747]" onClick={() => onRevoke(inv.id)} data-testid={`invite-revoke-${inv.id}`}>
                    <Trash2 size={14} className="mr-1" /> Revoke
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
